import type { AddProductSopSection } from "../types";

export const ADD_PRODUCT_SOP_SECTIONS: AddProductSopSection[] = [
  {
    id: "before-you-start",
    titleKey: "add-product-sop-before-start-title",
    titleFallback: "Before you start",
    steps: [
      {
        bodyKey: "add-product-sop-before-start-1",
        bodyFallback:
          "Sign in and go to Portal → Inventory → Products. You need permission to manage products."
      },
      {
        bodyKey: "add-product-sop-before-start-2",
        bodyFallback:
          "Every product must belong to a category. Create your categories first under Categories, or add a new category while filling out the product form."
      },
      {
        bodyKey: "add-product-sop-before-start-3",
        bodyFallback:
          "A product is your catalog record. After saving the product, you will add product items to record the actual stock available in each store."
      }
    ]
  },
  {
    id: "open-form",
    titleKey: "add-product-sop-open-form-title",
    titleFallback: "Open Add New Product",
    steps: [
      {
        bodyKey: "add-product-sop-open-form-1",
        bodyFallback:
          "On the Products page, click the purple Add New Product button. The Add New Product form will open.",
        imageId: "open-modal"
      },
      {
        bodyKey: "add-product-sop-open-form-2",
        bodyFallback:
          "Enter the Product Name. This is the name your staff will see when searching, selling, or managing the product. For example: Cordless drill 18V or Electrical cable 2.5 mm²."
      }
    ]
  },
  {
    id: "selling-mode",
    titleKey: "add-product-sop-selling-mode-title",
    titleFallback: "Choose selling mode",
    steps: [
      {
        bodyKey: "add-product-sop-selling-mode-1",
        bodyFallback:
          "Open Selling Mode and choose how this product should be sold at checkout.",
        imageId: "selling-mode"
      },
      {
        bodyKey: "add-product-sop-selling-mode-2",
        bodyFallback:
          "Single Item: Use this when the product is sold as one complete item, such as a drill, appliance, or sealed pack."
      },
      {
        bodyKey: "add-product-sop-selling-mode-3",
        bodyFallback:
          "Unit Based (Piece): Use this for countable items such as screws, bottles, boxes, packets, or pieces."
      },
      {
        bodyKey: "add-product-sop-selling-mode-4",
        bodyFallback:
          "Measured Quantity: Use this when the product is sold by measurement, such as weight, volume, or length. For example, cable by the meter or paint by the liter."
      }
    ]
  },
  {
    id: "store-category",
    titleKey: "add-product-sop-store-category-title",
    titleFallback: "Store & category",
    steps: [
      {
        bodyKey: "add-product-sop-store-category-1",
        bodyFallback:
          "Select Store. Company admins can choose the location where the product will be available. Store-level users will usually see only their assigned store."
      },
      {
        bodyKey: "add-product-sop-store-category-2",
        bodyFallback:
          "Select Category. This field is required. Use the dropdown to choose an existing category.",
        imageId: "category"
      },
      {
        bodyKey: "add-product-sop-store-category-3",
        bodyFallback:
          "If the category does not exist yet, click the + button beside Category to create a new category without leaving the product form.",
        imageId: "new-category"
      },
      {
        bodyKey: "add-product-sop-store-category-4",
        bodyFallback:
          "In the New Category form, enter the Category Name, add an optional Description, choose an optional Parent Category, then click Create Category.",
        imageId: "new-category-form"
      }
    ]
  },
  {
    id: "details",
    titleKey: "add-product-sop-details-title",
    titleFallback: "Brand, supplier & description",
    steps: [
      {
        bodyKey: "add-product-sop-details-1",
        bodyFallback: "Enter the Brand Name. This field is required."
      },
      {
        bodyKey: "add-product-sop-details-2",
        bodyFallback:
          "Optionally select a Supplier from the dropdown. If the supplier is not listed, click the + button beside the field to create a new supplier and link it to the product immediately."
      },
      {
        bodyKey: "add-product-sop-details-3",
        bodyFallback:
          "Add a clear Description so your team understands what the product is, how it is sold, or any important details they should know."
      }
    ]
  },
  {
    id: "base-unit",
    titleKey: "add-product-sop-base-unit-title",
    titleFallback: "Base unit",
    steps: [
      {
        bodyKey: "add-product-sop-base-unit-1",
        bodyFallback:
          "For Single Item and Unit Based (Piece) products, the base unit is fixed as Unit."
      },
      {
        bodyKey: "add-product-sop-base-unit-2",
        bodyFallback:
          "For Measured Quantity products, open Base Unit and choose how the product is stocked and sold. For example, use Meter (m) for cable, Kilogram (kg) for nails sold by weight, or Liter (L) for paint.",
        imageId: "base-unit-list"
      },
      {
        bodyKey: "add-product-sop-base-unit-3",
        bodyFallback:
          "Choose the unit that matches how your team sells the product day to day. Staff will enter quantities using this unit at checkout.",
        imageId: "base-unit-selected"
      }
    ]
  },
  {
    id: "partial-selling",
    titleKey: "add-product-sop-partial-selling-title",
    titleFallback: "Partial selling allowed",
    steps: [
      {
        bodyKey: "add-product-sop-partial-selling-1",
        bodyFallback:
          "Single Item products do not use this setting. Each sale is always for one complete item.",
        imageId: "partial-selling"
      },
      {
        bodyKey: "add-product-sop-partial-selling-2",
        bodyFallback:
          "For Unit Based (Piece) and Measured Quantity products, set Partial Selling Allowed to Yes when customers can buy only part of a stock line. For example, a customer may buy 40 screws from a box of 500, or 2.5 meters of cable from a longer roll."
      },
      {
        bodyKey: "add-product-sop-partial-selling-3",
        bodyFallback:
          "Choose No when the full quantity on a product item must be sold together."
      }
    ]
  },
  {
    id: "save",
    titleKey: "add-product-sop-save-title",
    titleFallback: "Save the product",
    steps: [
      {
        bodyKey: "add-product-sop-save-1",
        bodyFallback:
          "Review all required fields marked with *. If any validation message appears under a field, correct it before saving."
      },
      {
        bodyKey: "add-product-sop-save-2",
        bodyFallback:
          "Click Add Product to save. The product will appear in your catalog. Next, add product items to record stock, print QR labels, and start selling."
      }
    ]
  }
];
